import { CostCarrierConfig } from '@app/icare/entities/costCarrier/config'
import { patientCostCarrierQueries } from '@app/icare/entities/costCarrier/queries/patientCostCarrierQueries'
import { PatientCostCarrierEditDrawer } from '@app/icare/entities/patient/edits/PatientCostCarrierEditDrawer'
import {
  patientFindingsQueryOptions,
  selectByGroupAndGroupByRelationId,
} from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import type { PatientCostCarrierRelation } from '@app/icare/entities/patient/schema/PatientCostCarrier'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { RelationTabs } from '@app/icare/routes/_app/patienten/$id/_edit/-components/RelationTabs'
import { useRelationCommitNavigation } from '@app/icare/routes/_app/patienten/$id/_edit/-components/useRelationCommitNavigation'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_costCarrier/kostentraeger')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(patientCostCarrierQueries.forTabs(params.id)),
      queryClient.ensureQueryData(patientFindingsQueryOptions(params.id)),
    ]),
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: relations = [] } = useSuspenseQuery(patientCostCarrierQueries.forTabs(id))
  const { data: findings = {} } = useQuery({
    ...patientFindingsQueryOptions(id),
    select: selectByGroupAndGroupByRelationId('costCarriers'),
  })
  const { data: totalCount } = useQuery(patientCostCarrierQueries.totalCount(id))

  const handleAfterCommit = useRelationCommitNavigation<PatientCostCarrierRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: relation =>
      navigate({
        to: '/patienten/$id/kostentraeger/$relation',
        params: { id, relation: relation.id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '.', replace: true }),
  })

  return (
    <RelationTabs
      relations={relations}
      relationTotalCount={totalCount}
      basePath={`/patienten/${id}/kostentraeger`}
      findings={findings.global}
      findingsGroup="costCarriers"
      getLabel={item => CostCarrierConfig.format.primary(item.to) ?? t`Kostenträger`}
      onAdd={params =>
        void PatientCostCarrierEditDrawer.show({
          patientId: id,
          onAfterCommit: handleAfterCommit,
          ...params,
        })
      }
      addLabel={t`Kostenträger hinzufügen`}
    />
  )
}
